import { useId, useState, type ReactNode } from 'react'

/**
 * An information button that explains the thing beside it. The explanation
 * opens on hover, on keyboard focus, and on a click or tap that keeps it open.
 * The pointer can move onto the explanation without it closing, and Escape
 * dismisses it (WCAG 2.1 1.4.13).
 */
export default function InfoButton({ label, children }: { label: string; children: ReactNode }) {
  const popoverId = useId()
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const visible = !dismissed && (hovered || focused || pinned)

  return (
    <span
      className="relative inline-flex print:hidden"
      onMouseEnter={() => {
        setHovered(true)
        setDismissed(false)
      }}
      onMouseLeave={() => {
        setHovered(false)
        setDismissed(false)
      }}
      onFocus={() => {
        setFocused(true)
        setDismissed(false)
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false)
          setPinned(false)
          setDismissed(false)
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && visible) {
          event.stopPropagation()
          setPinned(false)
          setDismissed(true)
        }
      }}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={visible}
        aria-controls={popoverId}
        onClick={() => {
          // A click pins the explanation open; a click on a pinned one closes
          // it even while hover or focus would keep it showing.
          setPinned(!pinned)
          setDismissed(pinned)
        }}
        className="flex size-8 translate-y-0.5 items-center justify-center rounded-control text-ink-muted hover:bg-surface-2 hover:text-ink"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </svg>
      </button>
      <div
        id={popoverId}
        hidden={!visible}
        className="absolute top-full left-0 z-30 mt-1 w-[min(22rem,calc(100vw-2rem))] rounded-card border border-line bg-canvas p-4 text-small font-normal text-ink shadow-lg"
      >
        {children}
      </div>
    </span>
  )
}
